import { useEffect, useState } from 'react'

type Options = { defaultMatches?: boolean }

/**
 * React hook to evaluate a CSS media query and return whether it matches.
 *
 * Usage example:
 *   const isDesktop = useMediaQuery('(min-width: 960px)')
 *
 * @param query - A valid CSS media query string
 *   e.g. '(min-width: 960px)', '(max-width: 600px)'
 *
 * @param defaultMatches - Initial match value used during SSR (server-side rendering).
 *   - SSR에서는 `window.matchMedia`를 사용할 수 없으므로 기본값을 사용합니다.
 *   - 기본값은 false이며, 필요하다면 UA 기반으로 true/false를 전달할 수 있습니다.
 *   - 예: `useMediaQuery('(min-width: 960px)', { defaultMatches: isDesktopUA })`
 *
 * ⚠️ 주의
 * - query에는 반드시 완전한 미디어 문자열(@media 없이)만 넣어야 합니다.
 *   예: "tabletBreakPoint" 같은 상수가 있다면, 실제 문자열 "(min-width: 960px)" 형태로 전달해야 합니다.
 * - defaultMatches는 선택값이며, SSR 환경에서 깜빡임(mismatch)을 줄이려면
 *   UA(user agent) 기반 값을 넘겨주는 것이 좋습니다.
 */
const useMediaQuery = (
  query: string,
  { defaultMatches = false }: Options = {},
) => {
  const [matches, setMatches] = useState(defaultMatches)

  useEffect(() => {
    const mql = window.matchMedia(query)
    const onChange = () => {
      setMatches(mql.matches)
    }
    onChange()

    if (mql.addEventListener) {
      mql.addEventListener('change', onChange)
    } else {
      // Safari < 14 fallback
      mql.addListener(onChange)
    }

    return () => {
      if (mql.removeEventListener) {
        mql.removeEventListener('change', onChange)
      } else {
        mql.removeListener(onChange)
      }
    }
  }, [query])

  return matches
}

export default useMediaQuery
